import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from 'react';
import styled from 'styled-components';
import { isSameRange } from '../../../utils/range';
import { HistogramFilterInput } from './HistogramFilterInput';

type Mode = 'idle' | 'creating' | 'editing' | 'sliding';

type DomPosition = {
  parentTop: number;
  parentLeft: number;
  top: number;
  left: number;
  width: number;
  height: number;
};

type Props = {
  type: 'time' | 'duration' | 'distance' | 'count';
  range: [number, number] | null;
  min: number;
  max: number;
  step: number;
  plotAreaPosition: DomPosition | null;
  onPointerMove?: (event: PointerEvent<HTMLDivElement>) => void;
  onDetermine?: (range: [number, number] | null) => void;
};

const HistogramFilter = ({
  range,
  type,
  min,
  max,
  step,
  plotAreaPosition,
  onPointerMove,
  onDetermine,
}: Props): ReactNode => {
  const [localRange, setLocalRange] = useState<[number, number] | null>(range);
  const [mode, setMode] = useState<Mode>('idle');
  const [dragStartX, setDragStartX] = useState<number | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  // 外部から範囲が変更されたときにローカルの状態も更新する
  useEffect((): void => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalRange(range);
  }, [range]);

  const calculateValueFromPosition = useCallback(
    (x: number): number | null => {
      if (!plotAreaPosition) {
        return null;
      }
      // チャートの左端からの距離の割合を計算し、その割合に応じた値を返す
      // ただしチャートの外側までドラッグされた場合にも、範囲がチャートの最小値・最大値を超えないように補正する
      const percent = Math.min(
        Math.max((x - plotAreaPosition.left) / plotAreaPosition.width, 0),
        1
      );
      const value = percent * (max - min) + min;
      // 値がヒストグラムの刻み幅の倍数(かつ整数)になるように補正する
      return Math.round(Math.round(value / step) * step);
    },
    [plotAreaPosition, min, max, step]
  );

  const calculatePositionFromValue = useCallback(
    (range: [number, number] | null): { left: number; width: number } => {
      if (!plotAreaPosition || !range) {
        return { left: 0, width: 0 };
      }
      // 範囲がチャートの最小値・最大値を超えないように補正する
      const leftPercent = Math.min(
        Math.max((range[0] - min) / (max - min), 0),
        1
      );
      const widthPercent = Math.min(
        Math.max((range[1] - range[0]) / (max - min), 0),
        1
      );
      return {
        left: plotAreaPosition.left + leftPercent * plotAreaPosition.width,
        width: widthPercent * plotAreaPosition.width,
      };
    },
    [plotAreaPosition, min, max]
  );

  const filterPosition = calculatePositionFromValue(localRange ?? range);

  return (
    <>
      <PlotAreaOverlay
        ref={ref}
        mode={mode}
        left={
          plotAreaPosition
            ? plotAreaPosition.left - plotAreaPosition.parentLeft
            : 0
        }
        top={
          plotAreaPosition
            ? plotAreaPosition.top - plotAreaPosition.parentTop
            : 0
        }
        width={plotAreaPosition ? plotAreaPosition.width : 0}
        height={plotAreaPosition ? plotAreaPosition.height : 0}
        onPointerDown={(event: PointerEvent<HTMLDivElement>): void => {
          // 要素の外側でマウスを離しても補足できるようにキャプチャを設定する
          event.currentTarget.setPointerCapture(event.pointerId);

          if (!filterPosition || filterPosition.width === 0) {
            // まだ範囲が設定されていない場合は新規作成モードにする
            setMode('creating');
            setDragStartX(event.clientX);
            return;
          }
          if (
            filterPosition.left - 10 <= event.clientX &&
            event.clientX <= filterPosition.left + 10
          ) {
            // 範囲の左端(10px以内)をクリックした場合は右端を基準に編集モードにする
            setMode('editing');
            setDragStartX(filterPosition.left + filterPosition.width);
            return;
          }
          if (
            filterPosition.left + filterPosition.width - 10 <= event.clientX &&
            event.clientX <= filterPosition.left + filterPosition.width + 10
          ) {
            // 範囲の右端(10px以内)をクリックした場合は左端を基準に編集モードにする
            setMode('editing');
            setDragStartX(filterPosition.left);
            return;
          }
          if (
            filterPosition.left + 10 < event.clientX &&
            event.clientX < filterPosition.left + filterPosition.width - 10
          ) {
            // 範囲の内部をクリックした場合はスライドモードにする
            setMode('sliding');
            setDragStartX(event.clientX);
            return;
          }
          // 範囲の外をクリックした場合は新規作成モードにする
          setMode('creating');
          setDragStartX(event.clientX);
        }}
        onPointerMove={(event: PointerEvent<HTMLDivElement>): void => {
          // 指定があれば親コンポーネントなど別のコンポーネントにもポインターイベントを伝える
          if (onPointerMove) {
            onPointerMove(event);
          }

          if (mode === 'idle' || dragStartX === null) {
            return;
          }
          if (mode === 'creating' || mode === 'editing') {
            const startValue = calculateValueFromPosition(dragStartX);
            const currentValue = calculateValueFromPosition(event.clientX);
            if (
              startValue != null &&
              currentValue != null &&
              startValue !== currentValue
            ) {
              setLocalRange([
                Math.min(startValue, currentValue),
                Math.max(startValue, currentValue),
              ]);
            }
          }
          if (mode === 'sliding') {
            // ドラッグ開始位置からの差分を計算し、その差分だけ範囲を移動させる
            // ただし範囲がチャートの最小値・最大値を超えないように補正する
            const deltaX = Math.min(
              Math.max(
                event.clientX - dragStartX,
                (plotAreaPosition?.left ?? 0) - filterPosition.left
              ),
              (plotAreaPosition?.left ?? 0) +
                (plotAreaPosition?.width ?? 0) -
                filterPosition.left -
                filterPosition.width
            );
            const leftValue = calculateValueFromPosition(
              filterPosition.left + deltaX
            );
            const rightValue = calculateValueFromPosition(
              filterPosition.left + filterPosition.width + deltaX
            );
            if (leftValue != null && rightValue != null) {
              setLocalRange([leftValue, rightValue]);

              // フィルターの位置が更新されているためドラッグ開始位置も更新する
              setDragStartX(dragStartX + deltaX);
            }
          }
        }}
        onPointerUp={(event: PointerEvent<HTMLDivElement>): void => {
          // ポインターイベントのキャプチャを解除する
          event.currentTarget.releasePointerCapture(event.pointerId);

          setMode('idle');
          setDragStartX(null);
          if (onDetermine && !isSameRange(localRange, range)) {
            // ドラッグを終了したときに範囲が変更されていれば変更を確定する
            onDetermine(localRange);
          }
        }}
      >
        {filterPosition && plotAreaPosition && (
          <ColoredFilter
            mode={mode}
            top={0}
            left={filterPosition.left - plotAreaPosition.left}
            width={filterPosition.width}
            height="100%"
          />
        )}
      </PlotAreaOverlay>
      <HistogramFilterInput
        type={type}
        range={range}
        min={min}
        max={max}
        onDetermine={onDetermine}
      />
    </>
  );
};

const PlotAreaOverlay = styled('div').withConfig<{
  mode: Mode;
  left: number;
  top: number;
  width: number | string;
  height: number | string;
}>({
  shouldForwardProp: (prop): boolean =>
    !['mode', 'left', 'top', 'width', 'height'].includes(prop),
})`
  position: absolute;
  top: ${(props) => (props.top ? `${props.top}px` : 0)};
  left: ${(props) => (props.left ? `${props.left}px` : 0)};
  width: ${(props) =>
    typeof props.width === 'number' ? `${props.width}px` : props.width};
  height: ${(props) =>
    typeof props.height === 'number' ? `${props.height}px` : props.height};
  touch-action: none;
  cursor: ${(props): string => {
    if (props.mode === 'creating' || props.mode === 'editing') {
      return 'col-resize';
    }
    if (props.mode === 'sliding') {
      return 'grabbing';
    }
    return 'default';
  }};
`;

const ColoredFilter = styled('div').withConfig<{
  mode: Mode;
  left: number;
  top: number;
  width: number | string;
  height: number | string;
}>({
  shouldForwardProp: (prop): boolean =>
    !['mode', 'left', 'top', 'width', 'height'].includes(prop),
})`
  position: absolute;
  top: ${(props) => (props.top ? `${props.top}px` : 0)};
  left: ${(props) => (props.left ? `${props.left}px` : 0)};
  width: ${(props) =>
    typeof props.width === 'number' ? `${props.width}px` : props.width};
  height: ${(props) =>
    typeof props.height === 'number' ? `${props.height}px` : props.height};
  opacity: 0.5;
  background: #829ae3;
  cursor: ${(props): string => {
    if (props.mode === 'idle') {
      return 'grab';
    }
    if (props.mode === 'sliding') {
      return 'grabbing';
    }
    return 'default';
  }};
  ::before {
    content: '';
    position: absolute;
    top: 0;
    left: -5px;
    width: 10px;
    height: 100%;
    cursor: col-resize;
    z-index: 1;
  }
  ::after {
    content: '';
    position: absolute;
    top: 0;
    right: -5px;
    width: 10px;
    height: 100%;
    cursor: col-resize;
    z-index: 1;
  }
`;

export { HistogramFilter };
